import type { ReactElement } from "react";

import dayjs from "dayjs";
import ReactDOMServer from "react-dom/server";

import { combineConfigs } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";

import noLegendConfig from "pro-features/components/Chart/src/configs/legend/noLegendConfig";
import { algoChangeDateConfig } from "pro-features/components/Chart/src/configs/plotLines/algoChangeDateConfig";
import tooltipContainerConfig from "pro-features/components/Chart/src/configs/tooltip/tooltipContainerConfig";
import xAxisCrosshair from "pro-features/components/Chart/src/configs/xAxis/xAxisCrosshair";
import xAxisLabelsConfig from "pro-features/components/Chart/src/configs/xAxis/xAxisLabelsConfig";
import yAxisLabelsConfig from "pro-features/components/Chart/src/configs/yAxis/yAxisLabelsConfig";
import { toggleItems } from "app/components/widget/widget-utilities/time-granularity";
import type { granularityItem } from "app/components/widget/widget-utilities/time-granularity";
import { minAbbrNumberFilter } from "app/filters/ngFilters";
interface IData {
    x: number | string;
    y: number | string;
}
interface IChartData {
    color: string;
    data: IData[];
    name: string;
    index: number; // without the index the order of the lines in the chart will not be arranged correctly
    id: string;
}

interface IAreaChartConfig {
    chartData?: IChartData[];
    algoChangeDate?: number;
    height?: number;
    // @ts-ignore FIXME: strict migration - Highcharts.TooltipFormatterContextObject not exported from highcharts namespace
    tooltipFormatter?: (this: Highcharts.TooltipFormatterContextObject) => ReactElement;
    xAxisFormatter?: ({ value }: { value: FixmeType }) => string;
    yAxisFormatter?: ({ value }: { value: FixmeType }) => string;
    isMarkerEnabled?: boolean;
    timeGranularity?: granularityItem;
    additionalChartConfig?: object;
}

export const areaChartConfig = (args: IAreaChartConfig) => {
    const {
        chartData,
        algoChangeDate,
        height = 295,
        tooltipFormatter,
        xAxisFormatter = ({ value }: { value: FixmeType }) => dayjs.utc(value).utc().format(toggleItems.Monthly.format),
        yAxisFormatter = ({ value }: { value: FixmeType }) => minAbbrNumberFilter()(value),
        isMarkerEnabled = false,
        timeGranularity = toggleItems.Monthly,
        additionalChartConfig,
    } = args;
    const currentGranularity = timeGranularity.intervalConfig;
    const isChartStacking = false;
    // @ts-ignore FIXME: strict migration - combineConfigs accepts heterogeneous config shapes; array elements have varying types
    const chartConfig = combineConfigs({ xAxisFormatter, yAxisFormatter, data: chartData }, [
        noLegendConfig,
        // @ts-ignore FIXME: strict migration - xAxisCrosshair config shape mismatch
        xAxisCrosshair,
        xAxisLabelsConfig,
        yAxisLabelsConfig,
        tooltipContainerConfig,
        // @ts-ignore FIXME: strict migration - algoChangeDateConfig returns config with different shape
        algoChangeDate
            ? algoChangeDateConfig(algoChangeDate, {
                  showTooltip: false,
                  showDateTitle: false,
              })
            : {
                  xAxis: {
                      plotLines: null,
                  },
              },
        // @ts-ignore FIXME: strict migration - granularity intervalConfig type mismatch
        currentGranularity,
        {
            tooltip: {
                followPointer: false,
                shared: true,
                outside: true,
                useHTML: true,
                // @ts-ignore FIXME: strict migration - custom tooltipFormatter property
                tooltipFormatter,
                // @ts-ignore FIXME: strict migration - Highcharts.TooltipFormatterContextObject not exported from highcharts namespace
                formatter: function (this: Highcharts.TooltipFormatterContextObject) {
                    if (!tooltipFormatter) {
                        return;
                    }
                    return ReactDOMServer.renderToString(<>{tooltipFormatter.call(this)}</>);
                },
            },
            chart: {
                height: height,
                spacingTop: 10,
                plotBackgroundColor: "transparent",
                events: {},
                style: {
                    fontFamily: "Roboto",
                },
            },
            plotOptions: {
                line: {
                    lineWidth: 2,
                    connectNulls: false,
                },
                area: {
                    stacking: "normal",
                },
                series: {
                    turboThreshold: 5000,
                    // @ts-ignore FIXME: strict migration - fillOpacity is a valid Highcharts option but not in PlotSeriesOptions type
                    fillOpacity: 1,
                    marker: {
                        enabled: isMarkerEnabled,
                        symbol: "circle",
                    },
                    // @ts-ignore FIXME: strict migration - boolean to OptionsStackingValue conversion
                    stacking: isChartStacking as Highcharts.OptionsStackingValue,
                    animation: false,
                },
            },
            yAxis: {
                gridLineWidth: 0.5,
                showFirstLabel: true,
                showLastLabel: true,
                reversed: false,
                gridZIndex: 2,
                reversedStacks: true,
                tickPixelInterval: 50,
                labels: {
                    style: {
                        textTransform: "uppercase",
                        fontSize: "11px",
                        fontFamily: "Roboto",
                        color: colorsPalettes.carbon[200],
                    },
                },
            },
            xAxis: {
                gridLineWidth: 0,
                gridLineDashStyle: "Dash",
                tickLength: 5,
                labels: {
                    style: {
                        textTransform: "capitalize",
                        fontSize: "11px",
                        color: "#919191",
                        fontFamily: "Roboto",
                    },
                },
                minPadding: 0,
                maxPadding: 0,
            },
        },
        // @ts-ignore FIXME: strict migration - additionalChartConfig is object | undefined
        additionalChartConfig,
    ]);

    return chartConfig;
};
